// A folha do relatório especial — só desenho.
//
// Um relatório especial é uma correlação escrita sob encomenda: a família faz
// uma pergunta ("pode ser clônus?", "por que ela não lembra da gente?") e o
// documento cruza o que o sistema registrou dela com a literatura. Não nasce
// de regra nem de modelo: é texto datado, guardado como foi escrito, com as
// fontes ao lado. O corpo chega em Markdown restrito e vira blocos pelo
// `lerMarkdown` do shared — os mesmos blocos que o PDF desenha.
//
// Sem busca e sem estado: `Relatorio.tsx` traz os dados, e a ferramenta
// `conferir-relatorio-especial.mts` monta esta folha com um documento real
// para olhar no navegador antes de publicar.
import { Fragment, type ReactNode } from 'react';
import { formatarDataHora, lerMarkdown, textoPuro, type Bloco, type Trecho } from '@medicov1/shared';
import { ehSecaoDePerguntas, seccionar, tituloDaCarta, vaiParaMedico, type RelatorioEspecialDTO } from './relatorioEspecialTipos';
import { identificar } from './identificacao';

export type { RelatorioEspecialDTO, ResumoEspecialDTO } from './relatorioEspecialTipos';

export function Texto({ t }: { t: Trecho[] }) {
  return (
    <>
      {t.map((x, i) => {
        let n: ReactNode = x.texto;
        if (x.negrito) n = <b>{n}</b>;
        if (x.italico) n = <i>{n}</i>;
        if (x.link) n = <a href={x.link} target="_blank" rel="noreferrer">{n}</a>;
        return <Fragment key={i}>{n}</Fragment>;
      })}
    </>
  );
}

export function BlocoMd({ b }: { b: Bloco }) {
  switch (b.tipo) {
    case 'titulo':
      return <h3 className="doc__md__h3"><Texto t={b.texto} /></h3>;
    case 'paragrafo':
      return <p className="doc__md__p"><Texto t={b.texto} /></p>;
    case 'lista': {
      const itens = b.itens.map((it, i) => <li key={i}><Texto t={it} /></li>);
      return b.ordenada
        ? <ol className="doc__md__lista">{itens}</ol>
        : <ul className="doc__md__lista">{itens}</ul>;
    }
    case 'tabela': {
      // Com tres colunas ou mais, a tabela nao cabe na tela do celular e a
      // ultima coluna some atras da rolagem — justamente a coluna "o que
      // decide". Abaixo de 640px cada linha vira um cartao, com o nome da
      // coluna na frente de cada valor (o CSS le o data-rotulo).
      const empilha = b.cabecalho.length >= 3;
      const rotulo = (j: number) => (b.cabecalho[j] ? textoPuro(b.cabecalho[j]!) : '');
      return (
        <div className="doc__rolagem">
          <table className={`doc__tabela doc__md__tabela${empilha ? ' doc__md__tabela--empilha' : ''}`}>
            {b.cabecalho.length > 0 && (
              <thead>
                <tr>{b.cabecalho.map((c, i) => <th key={i}><Texto t={c} /></th>)}</tr>
              </thead>
            )}
            <tbody>
              {b.linhas.map((l, i) => (
                <tr key={i}>{l.map((c, j) => <td key={j} data-rotulo={rotulo(j)}><Texto t={c} /></td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    case 'citacao':
      return (
        <blockquote className="doc__md__citacao">
          {b.paragrafos.map((p, i) => <p key={i}><Texto t={p} /></p>)}
        </blockquote>
      );
    case 'regua':
      return <hr className="doc__md__regua" />;
  }
}

export function FolhaEspecial({ dados }: { dados: RelatorioEspecialDTO }) {
  // O que é para entregar à equipe identifica como o hospital; o resto, como a família.
  const quem = identificar(dados.paciente, vaiParaMedico(dados));
  const secoes = seccionar(lerMarkdown(dados.texto));
  // Pedido da família: carta para entregar ao hospital, não análise.
  const pedido = dados.tipo === 'pedido';

  if (pedido) {
    return (
      <article className="doc doc--especial doc--pedido">
        <header className="doc__cabeca">
          <div><h1>{tituloDaCarta(dados.titulo)}</h1></div>
          <div className="doc__selo"><span>Pedido da família</span></div>
        </header>
        {secoes.map((s, i) => (
          <section className="doc__bloco" key={i}>
            {s.titulo && <h2><Texto t={s.titulo} /></h2>}
            {s.blocos.map((b, j) => <BlocoMd b={b} key={j} />)}
          </section>
        ))}
      </article>
    );
  }

  return (
    <article className="doc doc--especial">
      <header className="doc__cabeca">
        <div>
          <h1>{quem.nome}</h1>
          <p className="doc__quem">{quem.linha}</p>
        </div>
        <div className="doc__selo">
          <span>Relatório especial</span>
          <small>análise de {formatarDataHora(new Date(dados.geradoEm))}</small>
          <small>registrado por {dados.autor}</small>
        </div>
      </header>

      <div className="doc__md__abertura">
        <h2 className="doc__md__titulo">{dados.titulo}</h2>
        <p className="doc__md__assunto"><b>O que motivou:</b> {dados.assunto}</p>
      </div>

      <p className="doc__aviso">
        {dados.fontes.length > 0
          ? 'Este documento cruza o que a família registrou no sistema com a literatura citada no fim. '
          : 'Este documento reúne o que a família registrou no sistema e os laudos do hospital. '}
        <b>Não é diagnóstico</b>: as hipóteses ficam para a avaliação da equipe, e a conduta é dela.
      </p>

      {secoes.map((s, i) => (
        <section className={`doc__bloco${ehSecaoDePerguntas(s) ? ' doc__bloco--perguntas' : ''}`} key={i}>
          {s.titulo && <h2><Texto t={s.titulo} /></h2>}
          {s.blocos.map((b, j) => <BlocoMd b={b} key={j} />)}
        </section>
      ))}

      {dados.fontes.length > 0 && (
        <section className="doc__bloco">
          <h2>Fontes</h2>
          <ol className="doc__fontes">
            {dados.fontes.map((f) => (
              <li key={f.url}><a href={f.url} target="_blank" rel="noreferrer">{f.titulo}</a><small>{f.url}</small></li>
            ))}
          </ol>
        </section>
      )}

      <footer className="doc__rodape">
        <p className="fraco">saudev1 · registro de apoio da família · não é o prontuário do hospital</p>
      </footer>
    </article>
  );
}
